import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DEFAULT_HOURS = DAYS.map(day => ({
  day,
  is_open: true,
  slots: [
    { open: '11:00', close: '21:00' },
  ],
}));

export default function Settings() {
  const [restaurant, setRestaurant] = useState(null);
  const [form, setForm] = useState({
    name: '', description: '', phone: '', address: '',
    city: '', cuisine_type: '', maps_link: '', plan: 'basic',
  });
  const [hours, setHours] = useState(DEFAULT_HOURS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingHours, setSavingHours] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [images, setImages] = useState([]);
  const [upgrading, setUpgrading] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [userPlan, setUserPlan] = useState('basic');
  const [planStatus, setPlanStatus] = useState('');
  const [planRenewsAt, setPlanRenewsAt] = useState('');
  const [trialEndsAt, setTrialEndsAt] = useState('');
  const [planPeriod, setPlanPeriod] = useState('monthly');
  const toast = useToast();

  // ── Detect success/cancel redirect from Lemon Squeezy ──────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success')) {
      toast.success('🎉 Payment successful! Your plan is being activated...');
      window.history.replaceState({}, '', '/admin/settings');
      setTimeout(() => window.location.reload(), 3000);
    }
    if (params.get('canceled')) {
      toast.error('Payment canceled. No charge was made.');
      window.history.replaceState({}, '', '/admin/settings');
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/restaurants'),
      api.get('/auth/me'),
    ]).then(([restData, userData]) => {
      if (restData.length > 0) {
        setRestaurant(restData[0]);
        setForm({ ...restData[0], plan: restData[0].plan || 'basic' });
        if (restData[0].opening_hours && restData[0].opening_hours.length > 0) {
          const migrated = restData[0].opening_hours.map(h => {
            if (h.slots) return h;
            return { day: h.day, is_open: h.is_open, slots: [{ open: h.open || '11:00', close: h.close || '22:00' }] };
          });
          setHours(migrated);
        }
        loadImages(restData[0].restaurant_id);
      }
      if (userData) {
        setUserPlan(userData.plan || 'basic');
        setPlanStatus(userData.plan_status || '');
        setPlanRenewsAt(userData.plan_renews_at || '');
        setPlanPeriod(userData.plan_period || 'monthly');
        setTrialEndsAt(userData.trial_ends_at || '');
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const loadImages = (rid) => {
    api.get(`/restaurants/${rid}/gallery`).then(setImages).catch(() => {});
  };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const setDayOpen = (dayIndex, value) => {
    setHours(prev => prev.map((h, i) => i === dayIndex ? { ...h, is_open: value } : h));
  };

  const setSlotValue = (dayIndex, slotIndex, field, value) => {
    setHours(prev => prev.map((h, i) => {
      if (i !== dayIndex) return h;
      const newSlots = h.slots.map((s, si) => si === slotIndex ? { ...s, [field]: value } : s);
      return { ...h, slots: newSlots };
    }));
  };

  const addSlot = (dayIndex) => {
    setHours(prev => prev.map((h, i) => {
      if (i !== dayIndex || h.slots.length >= 3) return h;
      return { ...h, slots: [...h.slots, { open: '17:00', close: '21:00' }] };
    }));
  };

  const removeSlot = (dayIndex, slotIndex) => {
    setHours(prev => prev.map((h, i) => {
      if (i !== dayIndex || h.slots.length <= 1) return h;
      return { ...h, slots: h.slots.filter((_, si) => si !== slotIndex) };
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Restaurant name is required'); return; }
    setSaving(true);
    try {
      let res;
      if (restaurant) {
        res = await api.put(`/restaurants/${restaurant.restaurant_id}`, form);
        toast.success('Settings saved!');
      } else {
        res = await api.post('/restaurants', form);
        toast.success('Restaurant created! 🎉');
      }
      setRestaurant(res);
      setForm(res);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveHours = async () => {
    setSavingHours(true);
    try {
      await api.put(`/restaurants/${restaurant.restaurant_id}/hours`, { opening_hours: hours });
      toast.success('Opening hours saved! ✅');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingHours(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    if (images.length + files.length > 8) { toast.error('Maximum 8 images allowed'); return; }
    setUploadingImage(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        await api.upload(`/restaurants/${restaurant.restaurant_id}/gallery`, fd);
      }
      loadImages(restaurant.restaurant_id);
      toast.success(`${files.length} image(s) uploaded! 🎉`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleSetCover = async (imageId) => {
    try {
      const res = await api.put(`/restaurants/${restaurant.restaurant_id}/gallery/${imageId}/set-cover`, {});
      setRestaurant(prev => ({ ...prev, cover_image_url: res.cover_image_url }));
      setImages(prev => prev.map(img => ({ ...img, is_cover: img.image_id === imageId })));
      toast.success('Cover image updated! ✅');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await api.delete(`/restaurants/${restaurant.restaurant_id}/gallery/${imageId}`);
      loadImages(restaurant.restaurant_id);
      toast.success('Image deleted!');
    } catch (err) {
      toast.error(err.message);
    }
  };

  // ── Payment Handlers ─────────────────────────────────────────────────
  const getVariantId = (plan, period) => {
    const variantMap = {
      basic_monthly:  process.env.REACT_APP_LS_BASIC_MONTHLY_VARIANT,
      basic_annual:   process.env.REACT_APP_LS_BASIC_ANNUAL_VARIANT,
      pro_monthly:    process.env.REACT_APP_LS_PRO_MONTHLY_VARIANT,
      pro_annual:     process.env.REACT_APP_LS_PRO_ANNUAL_VARIANT,
    };
    return variantMap[`${plan}_${period}`] || '';
  };

  const handleUpgrade = async (plan, period) => {
    const variantId = getVariantId(plan, period);
    if (!variantId) {
      toast.error('Plan not configured. Please contact support.');
      return;
    }
    setUpgrading(true);
    try {
      const data = await api.post('/billing/checkout', { variant_id: variantId });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUpgrading(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      const data = await api.get('/billing/portal');
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error('No active subscription found. Please upgrade first.');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

    const isPaidPlan = planStatus === 'active' || planStatus === 'past_due';
  const isOnTrial = planStatus === 'trial';
  const trialDaysLeft = isOnTrial && trialEndsAt
    ? Math.max(0, Math.floor((new Date(trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  if (loading) return <AdminLayout title="Settings"><div className="loading-center"><div className="loading-spinner"></div></div></AdminLayout>;

  return (
    <AdminLayout title="Settings">
      <div style={{ maxWidth: 700 }}>

        {/* ── Subscription Plan ── */}
        <div className="card mb-6">
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2 className="card-title">💳 Subscription Plan</h2>
            {isPaidPlan && planRenewsAt && (
              <span style={{ fontSize: 12, color: '#6b7280' }}>
                Renews {formatDate(planRenewsAt)}
              </span>
            )}
          </div>
          <div className="card-body">

            {/* ── Trial Banner ── */}
            {isOnTrial && (
              <div style={{ background: '#f0fdf4', border: '1.5px solid #4ade80', borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 20 }}>🎁</span>
                      <span style={{ fontSize: 16, fontWeight: 700, color: '#16a34a' }}>Pro Trial — {trialDaysLeft} days left</span>
                    </div>
                    <p style={{ fontSize: 13, color: '#166534', margin: 0 }}>
                      You have full Pro access. Upgrade before your trial ends to keep all features.
                    </p>
                  </div>
                  <button
                    onClick={() => handleUpgrade('pro', 'monthly')}
                    disabled={upgrading}
                    style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                  >
                    ⭐ Upgrade to Pro — $39.99/mo
                  </button>
                </div>
              </div>
            )}

            {/* ── Active Pro Plan ── */}
            {userPlan === 'pro' && isPaidPlan ? (
              <div style={{ background: '#fffbeb', border: '1.5px solid #f59e0b', borderRadius: 12, padding: '16px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 20 }}>⭐</span>
                      <span style={{ fontSize: 16, fontWeight: 700, color: '#d97706' }}>Pro Plan — Active</span>
                      {planStatus === 'past_due' && (
                        <span style={{ background: '#fee2e2', color: '#dc2626', fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>Payment Past Due</span>
                      )}
                    </div>
                    <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>
                      Online ordering, catering, email confirmations — all active.
                      {planRenewsAt && ` Next billing: ${formatDate(planRenewsAt)}`}
                    </p>
                  </div>
                  <button
                    onClick={handleManageBilling}
                    style={{ padding: '8px 16px', background: 'white', border: '1.5px solid #f59e0b', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#d97706' }}
                  >
                    ⚙️ Manage Billing
                  </button>
                </div>
              </div>

            ) : userPlan === 'basic' && isPaidPlan ? (
              /* ── Active Basic Plan ── */
              <div>
                <div style={{ background: 'var(--primary-light)', border: '1.5px solid var(--primary)', borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 20 }}>✅</span>
                        <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--primary)' }}>Basic Plan — Active</span>
                        {planStatus === 'past_due' && (
                          <span style={{ background: '#fee2e2', color: '#dc2626', fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>Payment Past Due</span>
                        )}
                      </div>
                      <p style={{ fontSize: 13, color: '#555', margin: 0 }}>
                        Digital menu, QR code, gallery and analytics active.
                        {planRenewsAt && ` Next billing: ${formatDate(planRenewsAt)}`}
                      </p>
                    </div>
                    <button
                      onClick={handleManageBilling}
                      style={{ padding: '8px 16px', background: 'white', border: '1.5px solid var(--primary)', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: 'var(--primary)' }}
                    >
                      ⚙️ Manage Billing
                    </button>
                  </div>
                </div>

                {/* Upgrade to Pro from Basic */}
                <div style={{ background: '#fffbeb', border: '2px solid #f59e0b', borderRadius: 12, padding: '16px 20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#d97706', marginBottom: 4 }}>⭐ Upgrade to Pro</div>
                      <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>
                        Unlock online ordering, catering requests, and order email confirmations.
                      </p>
                    </div>
                    <button
                      onClick={() => handleUpgrade('pro', 'monthly')}
                      disabled={upgrading}
                      style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: upgrading ? 'not-allowed' : 'pointer', opacity: upgrading ? 0.7 : 1 }}
                    >
                      {upgrading ? '⏳ Redirecting...' : '⭐ Upgrade to Pro — $39.99/mo'}
                    </button>
                  </div>
                </div>
              </div>

            ) : (
              /* ── Free Plan — Show upgrade options ── */
              <div>
                <div style={{ background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 18px', marginBottom: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 16 }}>🆓</span>
                    <span style={{ fontSize: 15, fontWeight: 700, color: '#374151' }}>Free Plan</span>
                  </div>
                  <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>
                    You are on the free plan. Upgrade to access all features and start accepting orders.
                  </p>
                </div>

                {/* Billing period toggle */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 20, background: '#f3f4f6', borderRadius: 10, padding: 4, width: 'fit-content' }}>
                  <button
                    onClick={() => setBillingPeriod('monthly')}
                    style={{ padding: '7px 18px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: billingPeriod === 'monthly' ? 'white' : 'transparent', color: billingPeriod === 'monthly' ? '#1a1a1a' : '#6b7280', boxShadow: billingPeriod === 'monthly' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none' }}
                  >Monthly</button>
                  <button
                    onClick={() => setBillingPeriod('annual')}
                    style={{ padding: '7px 18px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: billingPeriod === 'annual' ? 'white' : 'transparent', color: billingPeriod === 'annual' ? '#1a1a1a' : '#6b7280', boxShadow: billingPeriod === 'annual' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none', display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    Annual
                    <span style={{ background: '#dcfce7', color: '#16a34a', fontSize: 10, padding: '1px 6px', borderRadius: 20, fontWeight: 700 }}>SAVE 2 MONTHS</span>
                  </button>
                </div>

                {/* Plan cards */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>

                  {/* Basic Plan */}
                  <div style={{ border: '1.5px solid var(--border)', borderRadius: 12, padding: '18px 20px', background: 'white' }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#333', marginBottom: 4 }}>Basic</div>
                    <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--primary)', marginBottom: 2 }}>
                      {billingPeriod === 'monthly' ? '$19.99' : '$199'}
                      <span style={{ fontSize: 13, fontWeight: 400, color: '#888' }}>{billingPeriod === 'monthly' ? '/mo' : '/yr'}</span>
                    </div>
                    {billingPeriod === 'annual' && (
                      <div style={{ fontSize: 11, color: '#16a34a', fontWeight: 600, marginBottom: 8 }}>$16.58/mo — save $40</div>
                    )}
                    <div style={{ fontSize: 12, color: '#666', lineHeight: 1.8, marginBottom: 16 }}>
                      ✓ Digital QR menu<br />
                      ✓ Photo gallery<br />
                      ✓ Opening hours<br />
                      ✓ 15 languages<br />
                      ✓ Spice level display<br />
                      ✓ Menu filters (Veg/Non-veg/Vegan)<br />
                      ✓ Unlimited QR scans<br />
                      ✓ Analytics<br />
                      ✗ Online ordering<br />
                      ✗ Catering requests
                    </div>
                    <button
                      onClick={() => handleUpgrade('basic', billingPeriod)}
                      disabled={upgrading}
                      style={{ width: '100%', padding: '10px', background: upgrading ? '#ccc' : 'var(--primary)', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: upgrading ? 'not-allowed' : 'pointer' }}
                    >
                      {upgrading ? '⏳ Redirecting...' : `Get Basic — ${billingPeriod === 'monthly' ? '$19.99/mo' : '$199/yr'}`}
                    </button>
                    {billingPeriod === 'annual' && (
                      <p style={{ fontSize: 11, color: '#999', marginTop: 8, textAlign: 'center' }}>Billed as $199/year</p>
                    )}
                  </div>

                  {/* Pro Plan */}
                  <div style={{ border: '2px solid #f59e0b', borderRadius: 12, padding: '18px 20px', background: '#fffbeb', position: 'relative' }}>
                    <div style={{ position: 'absolute', top: -10, right: 16, background: '#f59e0b', color: 'white', fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>MOST POPULAR</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#d97706', marginBottom: 4 }}>Pro</div>
                    <div style={{ fontSize: 26, fontWeight: 800, color: '#d97706', marginBottom: 2 }}>
                      {billingPeriod === 'monthly' ? '$39.99' : '$399'}
                      <span style={{ fontSize: 13, fontWeight: 400, color: '#888' }}>{billingPeriod === 'monthly' ? '/mo' : '/yr'}</span>
                    </div>
                    {billingPeriod === 'annual' && (
                      <div style={{ fontSize: 11, color: '#16a34a', fontWeight: 600, marginBottom: 8 }}>$33.25/mo — save $80</div>
                    )}
                    <div style={{ fontSize: 12, color: '#666', lineHeight: 1.8, marginBottom: 16 }}>
                      ✓ Everything in Basic<br />
                      ✓ Online ordering system<br />
                      ✓ Catering request management<br />
                      ✓ Auto order confirmation emails<br />
                      ✓ Real-time order notifications<br />
                      ✓ Order & scan analytics<br />
                      ✓ Priority support
                    </div>
                    <button
                      onClick={() => handleUpgrade('pro', billingPeriod)}
                      disabled={upgrading}
                      style={{ width: '100%', padding: '10px', background: upgrading ? '#d97706aa' : 'linear-gradient(135deg, #f59e0b, #d97706)', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: upgrading ? 'not-allowed' : 'pointer' }}
                    >
                      {upgrading ? '⏳ Redirecting...' : `⭐ Upgrade to Pro — ${billingPeriod === 'monthly' ? '$39.99/mo' : '$399/yr'}`}
                    </button>
                    {billingPeriod === 'annual' && (
                      <p style={{ fontSize: 11, color: '#888', marginTop: 8, textAlign: 'center' }}>Billed as $399/year</p>
                    )}
                  </div>
                </div>

                <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 12, textAlign: 'center' }}>
                  🔒 30-day free trial on all plans · Card required · Cancel anytime · Powered by Lemon Squeezy (Stripe)
                </p>
              </div>
            )}
          </div>
        </div>

        {/* ── Restaurant Images Gallery ── */}
        {restaurant && (
          <div className="card mb-6">
            <div className="card-header">
              <h2 className="card-title">🖼️ Restaurant Images</h2>
            </div>
            <div className="card-body">
              <p className="text-muted text-sm mb-4">
                Upload multiple photos. Click <strong>"Set as Cover"</strong> to choose the main banner.
              </p>
              {images.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
                  {images.map(img => (
                    <div key={img.image_id} style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', border: img.is_cover ? '3px solid var(--primary)' : '2px solid var(--border)' }}>
                      <img src={img.image_url} alt="Restaurant"
                        style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }}
                        onError={e => e.target.style.display = 'none'} />
                      {img.is_cover && (
                        <div style={{ position: 'absolute', top: 6, left: 6, background: 'var(--primary)', color: 'white', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20 }}>⭐ COVER</div>
                      )}
                      <div style={{ padding: '6px', display: 'flex', gap: 4, flexDirection: 'column', background: 'white' }}>
                        {!img.is_cover && (
                          <button className="btn btn-outline btn-sm" style={{ fontSize: 11, padding: '3px 6px' }} onClick={() => handleSetCover(img.image_id)}>⭐ Set as Cover</button>
                        )}
                        <button className="btn btn-danger btn-sm" style={{ fontSize: 11, padding: '3px 6px' }} onClick={() => handleDeleteImage(img.image_id)}>🗑️ Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ height: 140, background: 'var(--primary-light)', borderRadius: 10, border: '2px dashed var(--primary)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginBottom: 16, color: 'var(--primary)' }}>
                  <span style={{ fontSize: 36 }}>🖼️</span>
                  <p style={{ marginTop: 8, fontSize: 14 }}>No images yet — upload some!</p>
                </div>
              )}
              <div className="flex gap-2 items-center flex-wrap">
                <button className="btn btn-primary" onClick={() => document.getElementById('gallery-upload').click()} disabled={uploadingImage}>
                  {uploadingImage ? 'Uploading...' : '📷 Upload Images'}
                </button>
                <span className="text-muted text-sm">{images.length}/8 images • Max 5MB each</span>
                <input id="gallery-upload" type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleImageUpload} />
              </div>
            </div>
          </div>
        )}

        {/* ── Opening Hours ── */}
        {restaurant && (
          <div className="card mb-6">
            <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 className="card-title">🕐 Opening Hours</h2>
              <button className="btn btn-primary btn-sm" onClick={handleSaveHours} disabled={savingHours}>
                {savingHours ? 'Saving...' : '💾 Save'}
              </button>
            </div>
            <div className="card-body" style={{ padding: '12px 20px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {hours.map((h, dayIndex) => (
                  <div key={h.day} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 8,
                    padding: '8px 10px', borderRadius: 10,
                    background: h.is_open ? 'var(--primary-light)' : '#f9f9f9',
                    border: `1px solid ${h.is_open ? '#4ade80' : 'var(--border)'}`,
                  }}>
                    {/* Day toggle + name */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 80, paddingTop: 2 }}>
                      <label className="toggle" style={{ flexShrink: 0 }}>
                        <input type="checkbox" checked={h.is_open} onChange={e => setDayOpen(dayIndex, e.target.checked)} />
                        <span className="toggle-slider"></span>
                      </label>
                      <span style={{ fontWeight: 700, fontSize: 13, color: h.is_open ? 'var(--primary)' : '#999' }}>
                        {h.day.slice(0, 3)}
                      </span>
                    </div>

                    {/* Closed state */}
                    {!h.is_open && (
                      <span style={{ fontSize: 13, color: '#bbb', fontStyle: 'italic', paddingTop: 4 }}>Closed</span>
                    )}

                    {/* Open state — slots */}
                      {h.is_open && (
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {h.slots.map((slot, slotIndex) => (
                          <div key={slotIndex} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <input type="time" value={slot.open}
                              onChange={e => setSlotValue(dayIndex, slotIndex, 'open', e.target.value)}
                              style={{ border: '1px solid var(--border)', borderRadius: 6, padding: '4px 4px', fontSize: 12, flex: 1, minWidth: 0, background: 'white', WebkitAppearance: 'none' }} />
                            <span style={{ color: '#888', fontSize: 11, flexShrink: 0 }}>–</span>
                            <input type="time" value={slot.close}
                              onChange={e => setSlotValue(dayIndex, slotIndex, 'close', e.target.value)}
                              style={{ border: '1px solid var(--border)', borderRadius: 6, padding: '4px 4px', fontSize: 12, flex: 1, minWidth: 0, background: 'white', WebkitAppearance: 'none' }} />
                            {h.slots.length > 1 && (
                              <button onClick={() => removeSlot(dayIndex, slotIndex)}
                                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 14, padding: '0 2px', flexShrink: 0 }}>✕</button>
                            )}
                          </div>
                        ))}
                        {h.slots.length < 3 && (
                          <button onClick={() => addSlot(dayIndex)}
                            style={{ alignSelf: 'flex-start', background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: 11, fontWeight: 600, padding: 0 }}>
                            + Add slot
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Restaurant Info ── */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">{restaurant ? 'Restaurant Settings' : 'Create Your Restaurant'}</h2>
          </div>
          <div className="card-body">
            <form onSubmit={handleSave} className="space-y-4">
              <div className="form-group">
                <label className="form-label">Restaurant Name *</label>
                <input className="form-input" value={form.name || ''} onChange={e => set('name', e.target.value)} placeholder="e.g. Persis Indian Restaurant" required />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-textarea" value={form.description || ''} onChange={e => set('description', e.target.value)} placeholder="Tell customers about your restaurant..." />
              </div>
              <div className="flex gap-4">
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Phone</label>
                  <input className="form-input" value={form.phone || ''} onChange={e => set('phone', e.target.value)} placeholder="+1 (555) 000-0000" />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Cuisine Type</label>
                  <input className="form-input" value={form.cuisine_type || ''} onChange={e => set('cuisine_type', e.target.value)} placeholder="e.g. Indian, Italian..." />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Address</label>
                <input className="form-input" value={form.address || ''} onChange={e => set('address', e.target.value)} placeholder="Street address" />
              </div>
              <div className="form-group">
                <label className="form-label">City</label>
                <input className="form-input" value={form.city || ''} onChange={e => set('city', e.target.value)} placeholder="City" />
              </div>
              <div className="form-group">
                <label className="form-label">🗺️ Google Maps Link</label>
                <input className="form-input" value={form.maps_link || ''} onChange={e => set('maps_link', e.target.value)} placeholder="https://maps.google.com/?q=your+restaurant" />
                <p style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
                  How to get it: Google Maps → Search your restaurant → Share → Copy link
                </p>
              </div>
              <div className="flex gap-4">
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">📸 Instagram URL</label>
                  <input className="form-input" value={form.instagram_url || ''} onChange={e => set('instagram_url', e.target.value)} placeholder="https://instagram.com/yourrestaurant" />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">👥 Facebook URL</label>
                  <input className="form-input" value={form.facebook_url || ''} onChange={e => set('facebook_url', e.target.value)} placeholder="https://facebook.com/yourrestaurant" />
                </div>
              </div>
              <button type="submit" className="btn btn-primary btn-lg" disabled={saving}>
                {saving ? 'Saving...' : restaurant ? 'Save Changes' : 'Create Restaurant'}
              </button>
            </form>
          </div>
        </div>

      </div>
    </AdminLayout>
  );
}